
import Vue from "vue";
import Component from "vue-class-component";
import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
import {
  Certificate,
  CertificatesClient,
  CertificateStatus,
  OfficialCertificateLightViewModel,
} from "@/api-client";
import ServersidePagingSelector from "@/components/ServersidePagingSelector.vue";
import { formatDate } from "@/formatting/dateFormatter";
import { BvTableFieldArray } from "bootstrap-vue";
import CertificateStatusBadge from "@/features/shared/components/CertificateStatusBadge.vue";
import ClientsidePagingSelector from "@/components/ClientsidePagingSelector.vue";
import Loader from "@/features/shared/components/Loader";
import ServersidePaging from "@/components/ServersidePaging";
import { debounceFn } from "debounce-decorator-ts";
import PopoverInfo from "@/components/PopoverInfo.vue";
import FilterCheckbox from "@/components/FilterCheckbox.vue";

class Filters {
  searchTerm: string;
  showInvalid!: boolean;
  showValid!: boolean;

  constructor(searchTerm?: string, showInvalid?:boolean, showValid?:boolean) {
    this.searchTerm = searchTerm || "";
    this.showInvalid = showInvalid || false;
    this.showValid = showValid || true;
  }
}

@Component({
  components: {
    CertificateStatusBadge,
    PopoverInfo,
    TableEmptySlot,
    ClientsidePagingSelector,
    ServersidePagingSelector,
    FilterCheckbox
  },
})
export default class CertificatesTable extends Vue {
  certificates: OfficialCertificateLightViewModel[] = [];

  dataLoaded = false;
  filters = new Filters();
  paging = new ServersidePaging();
  loader = new Loader(true);

  async created() {
    this.getParametersFromRouteOrDefault();

    this.$watch(
      () => this.filters,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      },
      { deep: true }
    );

    this.$watch(
      () => this.paging.pageNumber,
      () => {
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.paging.pageSize,
      () => {
        this.paging.pageNumber = 1;
        this.reflectParametersToRoute();
      }
    );

    this.$watch(
      () => this.$route.query,
      () => {
        this.getCertificates();
      },
      { deep: true, immediate: true }
    );
  }

 navigateToDetails(certificate: OfficialCertificateLightViewModel) {
  this.$router.push({
    name: 'certificate-details',
    params: {
      seafarerId: certificate.seafarerId,
      id: certificate.id,
    },
  });
}

  async getCertificates() {
    this.certificates = [];
    const result = await new CertificatesClient()
      .setLoader(this.loader)
      .certificates2(
        this.filters.searchTerm,
        this.paging.pageNumber,
        this.paging.pageSize,
        this.filters.showValid,
        this.filters.showInvalid,
        
      );
    this.paging.totalCount = result.totalCount;
    this.paging.pageSize = result.pageSize;
    this.certificates = result.pageResult;
    this.dataLoaded = true;
  }

  @debounceFn(100) // Prevents possible double-update
  reflectParametersToRoute(): void {
    this.$router.replace({
      query: {
        // Setting to 'undefined' removes parameter from URL.
        searchTerm: this.filters.searchTerm || undefined,
        pageNumber: this.paging.pageNumber
          ? this.paging.pageNumber.toString()
          : undefined,
        pageSize: this.paging.pageSize
          ? this.paging.pageSize.toString()
          : undefined,
        showInvalid: this.filters.showInvalid.toString(),
        showValid: this.filters.showValid.toString()

      },
    });
  }

  getParametersFromRouteOrDefault() {
    const route = this.$route as any;
    const searchTerm: string | undefined = route.query.searchTerm;
    const showInvalid: boolean | undefined = route.query.showInvalid;
    const showValid: boolean | undefined = route.query.showValid;

    this.filters = new Filters(searchTerm,showInvalid,showValid);

    this.paging.pageNumber =
      parseInt(route.query.pageNumber) || this.paging.pageNumber;
    this.paging.pageSize =
      parseInt(route.query.pageSize) || this.paging.pageSize;
  }

  fields: BvTableFieldArray = [
    {
      key: "documentNumber",
      label: this.$ls.Global_Label_DocumentNumber,
    },
    {
      key: "seafarerName",
      label: this.$ls.Global_Label_Seafarer,
    },
    {
      key: "birthDate",
      label: this.$ls.Global_Label_DateOfBirth,
      formatter: (value: any, key: string, item: OfficialCertificateLightViewModel): string =>
        formatDate(item.birthDate),
    },
    {
      key: "name",
      label: this.$ls.Global_Label_Name,
      thClass: "w-20",
      tdClass: "certificate-name",
    },
    {
      key: "issuedAt",
      label: this.$ls.Global_Label_IssueDateShort,
      formatter: (value: any, key: string, item: OfficialCertificateLightViewModel): string =>
        formatDate(item.issuedAt),
    },
    {
      key: "validUntil",
      label: this.$ls.Global_Label_ValidUntil,
      formatter: (value: any, key: string, item: OfficialCertificateLightViewModel): string =>
        formatDate(item.validUntil),
    },
    {
      key: "statusString",
      label: this.$ls.Global_Label_DocumentState,
    },
    {
      key: "linkIcon",
      label: "",
      thClass: "w-5",
      tdClass: "table-actions",
    },
  ];
}
